import React from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SignalWifiOffIcon from '@mui/icons-material/SignalWifiOff';
import { renderWidget } from './registry/dtWidgetRegistry';
import { useDTWidgetData } from './hooks/useDTWidgetData';
import {
  buildDTWidgetCardSx,
  dtWidgetHeaderSx,
  dtWidgetTitleSx,
  staleOverlaySx,
  disconnectedOverlaySx,
} from './styles/dtWidgetStyleTokens';

function DTWidgetCard({ widget, isEditMode, onEdit, onDelete, onClone, theme, componentData }) {
  const isDark = theme === 'dark';
  const basic = widget?.props?.basic || {};
  const tagprops = widget?.props?.extended?.tagprops || [];

  const { liveData, isStale, isDisconnected } = useDTWidgetData(tagprops, componentData);

  const showWidgetHeader = isEditMode || isStale || isDisconnected;
  const overlayTop = showWidgetHeader ? 32 : 0;

  return (
    <Box sx={buildDTWidgetCardSx(basic, theme)}>
      {/* Header: title only in edit mode; status icons / actions when needed */}
      {showWidgetHeader && (
      <Box
        sx={{
          ...dtWidgetHeaderSx(isDark),
          ...(!isEditMode ? { justifyContent: 'flex-end' } : {}),
        }}
      >
        {isEditMode && (
          <Typography sx={dtWidgetTitleSx(basic, isDark)} title={basic.widgetName || widget.type}>
            {basic.widgetName || widget.type}
          </Typography>
        )}
        <Box className="widget-action-buttons" sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
          {isStale && !isDisconnected && (
            <Tooltip title="Data may be outdated">
              <WarningAmberIcon fontSize="small" sx={{ color: '#ffb300' }} />
            </Tooltip>
          )}
          {isDisconnected && (
            <Tooltip title="Gateway offline">
              <SignalWifiOffIcon fontSize="small" sx={{ color: '#f44336' }} />
            </Tooltip>
          )}
          {isEditMode && (
            <>
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => onEdit?.(widget)}>
                  <EditIcon sx={{ fontSize: 14, color: isDark ? '#aaa' : '#666' }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Clone">
                <IconButton size="small" onClick={() => onClone?.(widget)}>
                  <ContentCopyIcon sx={{ fontSize: 14, color: isDark ? '#aaa' : '#666' }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton size="small" onClick={() => onDelete?.(widget.id)}>
                  <DeleteIcon sx={{ fontSize: 14, color: '#f44336' }} />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>
      )}

      {/* Content: minHeight 0 + inner flex column lets children fill grid height (no inner scroll). */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: basic.scrollMode === 'fixed' ? 'hidden' : 'auto',
          position: 'relative',
        }}
      >
        {isDisconnected && (
          <Box sx={{ ...disconnectedOverlaySx, top: overlayTop }}>
            <Typography variant="caption" sx={{ color: '#f44336', fontWeight: 700 }}>
              Gateway Offline
            </Typography>
          </Box>
        )}
        {isStale && !isDisconnected && (
          <Box sx={{ ...staleOverlaySx, top: overlayTop }}>
            <Typography variant="caption" sx={{ color: '#ffb300', fontWeight: 700, opacity: 0.8 }}>
              Stale Data
            </Typography>
          </Box>
        )}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: basic.scrollMode === 'fixed' ? 'hidden' : 'auto',
            width: '100%',
          }}
        >
          {renderWidget(widget, liveData.length ? liveData : componentData, theme, isEditMode, {})}
        </Box>
      </Box>
    </Box>
  );
}

export default React.memo(DTWidgetCard);
